import { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ShoppingCart, Menu, X, Play, Pause, User, LogOut, Music, 
  Shirt, Car, Home, ChevronRight, Volume2, VolumeX, Shield, Heart, Package, Gift, Mic2, Music2, HelpCircle, Crown, Trophy, Radio, Bell, Sparkles, Swords, BarChart3, DollarSign, Flame, Rocket, Wifi, Film, MapPin, Palette, Fuel
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { ScrollArea } from "./components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";

// Capacitor native imports
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Check if native platform
const isNative = Capacitor.isNativePlatform();

// Initialize native features
const initNative = async () => {
  if (!isNative) return;
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#020204' });
    await SplashScreen.hide();
  } catch (e) {
    console.warn('Native init:', e);
  }
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setUser(response.data);
      } else {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      }
    } catch (error) {
      setUser(null);
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name, referral_code) => {
    const payload = { email, password, name };
    if (referral_code) payload.referral_code = referral_code;
    const response = await axios.post(`${API}/auth/register`, payload);
    localStorage.setItem("token", response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    localStorage.removeItem("token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, loginWithGoogle, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Cart Context
const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchCart = useCallback(async () => {
    if (!user) {
      setCart({ items: [], total: 0 });
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/cart`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setCart(response.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  }, [user]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (item) => {
    if (!user) {
      toast.error("Please login to add items to cart");
      return false;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/cart/add`, item, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      await fetchCart();
      toast.success("Added to cart!");
      return true;
    } catch (error) {
      toast.error("Failed to add to cart");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId, licenseType) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/cart/remove/${itemId}`, {
        params: { license_type: licenseType },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      await fetchCart();
      toast.success("Removed from cart");
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const clearCart = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/cart/clear`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setCart({ items: [], total: 0 });
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  };

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, removeFromCart, clearCart, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      
      if (sessionId) {
        try {
          const response = await axios.get(`${API}/auth/session`, {
            params: { session_id: sessionId },
            withCredentials: true
          });
          setUser(response.data.user);
          toast.success("Welcome back!");
          navigate('/', { replace: true, state: { user: response.data.user } });
        } catch (error) {
          toast.error("Authentication failed");
          navigate('/');
        }
      } else {
        navigate('/');
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#020204] flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-neon-cyan border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-zinc-400">Authenticating...</p>
      </div>
    </div>
  );
};

// Navbar Component
const NavDropdown = ({ label, items }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="text-zinc-400 hover:text-neon-cyan transition-colors uppercase tracking-wider text-xs font-medium flex items-center gap-1"
        data-testid={`nav-${label.toLowerCase()}`}
      >
        {label}
        <ChevronRight className={`w-3 h-3 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 pt-2 z-50">
          <div className="glass border border-white/10 rounded-lg py-2 min-w-[180px] shadow-xl shadow-black/50">
            {items.map((item) => (
              <Link key={item.href} to={item.href}
                className="flex items-center gap-2.5 px-4 py-2 text-xs text-zinc-400 hover:text-neon-cyan hover:bg-white/5 transition"
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <item.icon className="w-3.5 h-3.5" />
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const { user, logout, loginWithGoogle } = useAuth();
  const { cart } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [loyaltyPoints, setLoyaltyPoints] = useState(null);
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const [showNotifs, setShowNotifs] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      axios.get(`${API}/loyalty/me`, { headers, withCredentials: true })
        .then(res => setLoyaltyPoints(res.data)).catch(() => {});
      axios.get(`${API}/notifications/inbox`, { headers, withCredentials: true })
        .then(res => setNotifications(res.data)).catch(() => {});
    }
  }, [user]);

  const navLinks = [
    { href: "/", label: "Home", icon: Home },
    { href: "/beats", label: "Beats", icon: Music },
    { href: "/discover", label: "Discover", icon: Sparkles },
    { href: "/feed", label: "Feed", icon: Radio },
    { href: "/merch", label: "Merch", icon: Shirt },
    { href: "/sessions", label: "Live", icon: Wifi },
  ];

  const moreMenus = [
    {
      label: "Shop", items: [
        { href: "/octane", label: "Octane Rewards", icon: Fuel },
        { href: "/bundles", label: "Bundles", icon: Package },
        { href: "/gallery", label: "Design Gallery", icon: Palette },
        { href: "/merch-studio", label: "Design Your Own", icon: Sparkles },
        { href: "/soundkits", label: "Sound Kits", icon: Music2 },
        { href: "/gift-cards", label: "Gift Cards", icon: Gift },
        { href: "/sync", label: "Sync Licensing", icon: Film },
      ]
    },
    {
      label: "Community", items: [
        { href: "/tokyo", label: "Tokyo District", icon: MapPin },
        { href: "/battles", label: "Battles", icon: Swords },
        { href: "/contests", label: "Contests", icon: Trophy },
        { href: "/challenges", label: "Challenges", icon: Flame },
        { href: "/crowdfund", label: "Fan-Backed", icon: Rocket },
        { href: "/garage", label: "Garage", icon: Car },
        { href: "/blog", label: "Blog", icon: ChevronRight },
      ]
    },
    {
      label: "Producers", items: [
        { href: "/producers", label: "Directory", icon: Mic2 },
        { href: "/beat-lab", label: "Beat Lab", icon: Sparkles },
        { href: "/beat-club", label: "Beat Club", icon: Crown },
        { href: "/analytics", label: "Analytics", icon: BarChart3 },
        { href: "/royalties", label: "Royalties", icon: DollarSign },
      ]
    },
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-10 h-10 bg-neon-cyan rounded-sm flex items-center justify-center">
                <span className="text-black font-heading font-bold text-lg">GS</span>
              </div>
              <span className="hidden sm:block font-heading text-sm uppercase tracking-widest">
                Gas Station
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-zinc-400 hover:text-neon-cyan transition-colors uppercase tracking-wider text-xs font-medium"
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              {moreMenus.map((menu) => (
                <NavDropdown key={menu.label} label={menu.label} items={menu.items} />
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              {/* Cart */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="cart-button">
                    <ShoppingCart className="w-5 h-5" />
                    {cart.items.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-neon-cyan text-black text-xs font-bold rounded-full flex items-center justify-center">
                        {cart.items.length}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent className="bg-[#09090b] border-white/10 w-full sm:max-w-md">
                  <SheetHeader>
                    <SheetTitle className="font-heading text-white">Your Cart</SheetTitle>
                  </SheetHeader>
                  <CartDrawer />
                </SheetContent>
              </Sheet>

              {/* User */}
              {user ? (
                <div className="flex items-center gap-2">
              {/* Octane Badge */}
              {user && (
                <button
                  onClick={() => navigate('/octane')}
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-neon-yellow/10 border border-neon-yellow/30 rounded-sm hover:bg-neon-yellow/20 transition-colors group"
                  data-testid="octane-badge"
                >
                  <Fuel className="w-3.5 h-3.5 text-neon-yellow group-hover:scale-110 transition-transform" />
                  <span className="text-neon-yellow font-mono text-xs font-bold">
                    {loyaltyPoints?.current_points?.toLocaleString() ?? "..."}
                  </span>
                  <span className="text-zinc-500 text-[10px] uppercase tracking-wider">oct</span>
                </button>
              )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/wishlist')}
                    className="text-zinc-400 hover:text-red-500"
                    data-testid="wishlist-nav-button"
                  >
                    <Heart className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/orders')}
                    className="text-zinc-400 hover:text-neon-green"
                    data-testid="orders-nav-button"
                  >
                    <Package className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/gift-cards')}
                    className="text-zinc-400 hover:text-yellow-400"
                    data-testid="gift-cards-nav-button"
                  >
                    <Gift className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/support')}
                    className="text-zinc-400 hover:text-sky-400"
                    data-testid="support-nav-button"
                  >
                    <HelpCircle className="w-5 h-5" />
                  </Button>
                  <div className="relative">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowNotifs(!showNotifs)}
                      className="text-zinc-400 hover:text-neon-yellow relative"
                      data-testid="notifications-bell-button"
                    >
                      <Bell className="w-5 h-5" />
                      {notifications.unread_count > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                          {notifications.unread_count}
                        </span>
                      )}
                    </Button>
                    {showNotifs && (
                      <div className="absolute right-0 top-12 w-80 bg-zinc-900 border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notifications-dropdown">
                        <div className="p-3 border-b border-white/10 flex items-center justify-between">
                          <span className="text-sm font-bold text-white">Notifications</span>
                          {notifications.unread_count > 0 && (
                            <button
                              onClick={async () => {
                                const token = localStorage.getItem("token");
                                await axios.post(`${API}/notifications/mark-read`, {}, {
                                  headers: { Authorization: `Bearer ${token}` }, withCredentials: true
                                }).catch(() => {});
                                setNotifications(prev => ({
                                  ...prev,
                                  unread_count: 0,
                                  notifications: prev.notifications.map(n => ({ ...n, read: true }))
                                }));
                              }}
                              className="text-xs text-neon-cyan hover:underline"
                              data-testid="mark-all-read-btn"
                            >
                              Mark all read
                            </button>
                          )}
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          {notifications.notifications.length === 0 ? (
                            <p className="text-zinc-500 text-sm text-center py-6">No notifications</p>
                          ) : notifications.notifications.slice(0, 10).map(n => (
                            <div
                              key={n.notification_id}
                              className={`px-3 py-2.5 border-b border-white/5 ${!n.read ? "bg-neon-cyan/5" : ""}`}
                            >
                              <p className="text-xs font-medium text-white">{n.title}</p>
                              <p className="text-xs text-zinc-400">{n.message}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/profile')}
                    className="text-zinc-400 hover:text-neon-magenta"
                    data-testid="profile-nav-button"
                  >
                    <User className="w-5 h-5" />
                  </Button>
                  {user.is_admin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate('/admin')}
                      className="text-neon-green"
                      data-testid="admin-button"
                    >
                      <Shield className="w-5 h-5" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={logout}
                    className="text-zinc-400 hover:text-white"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-5 h-5" />
                  </Button>
                </div>
              ) : (
                <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid="login-button">
                      <User className="w-5 h-5" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#09090b] border-white/10 sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-white text-center">Welcome to the Gas Station</DialogTitle>
                    </DialogHeader>
                    <AuthForm onSuccess={() => setShowAuthDialog(false)} />
                  </DialogContent>
                </Dialog>
              )}

              {/* Mobile Menu */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                data-testid="mobile-menu-button"
              >
                {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden glass border-t border-white/10"
            >
              <div className="px-4 py-4 space-y-1 max-h-[70vh] overflow-y-auto">
                {/* Mobile Octane Badge */}
                {user && (
                  <Link
                    to="/octane"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center justify-between px-4 py-3 mb-2 bg-neon-yellow/10 border border-neon-yellow/30 rounded-sm"
                    data-testid="mobile-octane-badge"
                  >
                    <div className="flex items-center gap-2">
                      <Fuel className="w-5 h-5 text-neon-yellow" />
                      <span className="text-neon-yellow font-bold uppercase tracking-wider text-sm">Octane</span>
                    </div>
                    <span className="text-neon-yellow font-mono font-bold text-sm">
                      {loyaltyPoints?.current_points?.toLocaleString() ?? "..."} OCT
                    </span>
                  </Link>
                )}
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-neon-cyan hover:bg-white/5 rounded-sm transition-colors"
                  >
                    <link.icon className="w-5 h-5" />
                    <span className="uppercase tracking-wider text-sm">{link.label}</span>
                  </Link>
                ))}
                {moreMenus.map((menu) => (
                  <div key={menu.label}>
                    <div className="px-4 pt-3 pb-1">
                      <span className="text-[10px] text-zinc-600 uppercase tracking-widest">{menu.label}</span>
                    </div>
                    {menu.items.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-zinc-400 hover:text-neon-cyan hover:bg-white/5 rounded-sm transition-colors"
                      >
                        <item.icon className="w-4 h-4" />
                        <span className="uppercase tracking-wider text-xs">{item.label}</span>
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
      <div className="h-16 md:h-20" /> {/* Spacer */}
    </>
  );
};

// Auth Form Component
const AuthForm = ({ onSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, loginWithGoogle } = useAuth();

  // Check URL for referral code
  useState(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      setReferralCode(ref);
      setIsLogin(false);
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success("Welcome back!");
      } else {
        await register(email, password, name, referralCode || undefined);
        toast.success("Account created!");
      }
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 py-4">
      <Button
        onClick={loginWithGoogle}
        className="w-full bg-white text-black hover:bg-zinc-200 font-medium py-6"
        data-testid="google-login-button"
      >
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
          <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Continue with Google
      </Button>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-white/10"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-[#09090b] text-zinc-500">or</span>
        </div>
      </div>

      <Tabs value={isLogin ? "login" : "register"} onValueChange={(v) => setIsLogin(v === "login")}>
        <TabsList className="grid w-full grid-cols-2 bg-zinc-900">
          <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
          <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
        </TabsList>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="bg-black/50 border-white/10 h-12"
                data-testid="name-input"
              />
            </div>
          )}
          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="referral" className="text-xs text-zinc-500">Referral Code (optional)</Label>
              <Input
                id="referral"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="GS______"
                className="bg-black/50 border-white/10 h-10 font-mono text-sm"
                data-testid="referral-code-input"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="bg-black/50 border-white/10 h-12"
              required
              data-testid="email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-black/50 border-white/10 h-12"
              required
              data-testid="password-input"
            />
          </div>
          <Button
            type="submit"
            className="w-full bg-neon-cyan text-black font-bold uppercase tracking-widest py-6 hover:bg-neon-cyan/90"
            disabled={loading}
            data-testid="auth-submit-button"
          >
            {loading ? "Loading..." : isLogin ? "Login" : "Create Account"}
          </Button>
        </form>
      </Tabs>
    </div>
  );
};

// Cart Drawer Component
const CartDrawer = () => {
  const { cart, removeFromCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = useState(false);

  const handleCheckout = async () => {
    if (!user) {
      toast.error("Please login to checkout");
      return;
    }
    if (cart.items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    setCheckingOut(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/payments/checkout/stripe`,
        { origin_url: window.location.origin, payment_method: "stripe" },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Checkout failed. Please try again.");
      setCheckingOut(false);
    }
  };

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <ShoppingCart className="w-12 h-12 text-zinc-600 mb-4" />
        <p className="text-zinc-400">Please login to view your cart</p>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <ShoppingCart className="w-12 h-12 text-zinc-600 mb-4" />
        <p className="text-zinc-400">Your cart is empty</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/beats')}
        >
          Browse Beats
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <ScrollArea className="flex-1 -mx-6 px-6">
        <div className="space-y-4 py-4">
          {cart.items.map((item, idx) => (
            <div
              key={`${item.item_id}-${item.license_type}-${idx}`}
              className="flex gap-4 p-4 bg-zinc-900/50 border border-white/5 rounded-sm"
            >
              {item.image && (
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-16 h-16 object-cover rounded-sm"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{item.name}</p>
                {item.license_type && (
                  <Badge variant="secondary" className="mt-1 text-xs uppercase">
                    {item.license_type}
                  </Badge>
                )}
                {item.size && (
                  <span className="text-xs text-zinc-400 block mt-1">
                    Size: {item.size}
                  </span>
                )}
                <p className="text-neon-cyan font-mono mt-2">${item.price?.toFixed(2)}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeFromCart(item.item_id, item.license_type)}
                className="text-zinc-400 hover:text-red-500"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="border-t border-white/10 pt-4 mt-4 space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-zinc-400">Total</span>
          <span className="text-2xl font-heading text-neon-cyan">${cart.total?.toFixed(2)}</span>
        </div>
        <Button
          onClick={handleCheckout}
          disabled={checkingOut}
          className="w-full bg-neon-cyan text-black font-bold uppercase tracking-widest py-6 btn-clip neon-cyan"
          data-testid="checkout-button"
        >
          {checkingOut ? "Processing..." : "Checkout with Stripe"}
        </Button>
      </div>
    </div>
  );
};

// Export all pages
export { AuthCallback };

function App() {
  // Initialize native features when app loads
  useEffect(() => {
    initNative();
  }, []);

  return (
    <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <div className="min-h-screen bg-[#020204] text-white">
            <div className="noise-overlay" />
            <AppRouter />
            <NewsletterPopup />
            <LiveChat />
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  background: '#09090b',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'white',
                },
              }}
            />
          </div>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

// App Router with OAuth detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <Navbar />
      <FlashSaleBanner />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/beats" element={<BeatsPage />} />
        <Route path="/beats/:beatId" element={<BeatDetailPage />} />
        <Route path="/merch" element={<MerchPage />} />
        <Route path="/merch/:merchId" element={<MerchDetailPage />} />
        <Route path="/garage" element={<GaragePage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/bundles" element={<BundlesPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/gift-cards" element={<GiftCardsPage />} />
        <Route path="/producers" element={<ProducerPage />} />
        <Route path="/producers/:producerId" element={<ProducerStorefrontPage />} />
        <Route path="/soundkits" element={<SoundKitsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/beat-club" element={<SubscriptionPage />} />
        <Route path="/contests" element={<ContestsPage />} />
        <Route path="/feed" element={<BeatFeedPage />} />
        <Route path="/beat-lab" element={<AIBeatsPage />} />
        <Route path="/customizer/:beatId" element={<BeatCustomizerPage />} />
        <Route path="/merch-studio" element={<MerchStudioPage />} />
        <Route path="/battles" element={<BattlesPage />} />
        <Route path="/analytics" element={<ProducerAnalyticsPage />} />
        <Route path="/royalties" element={<RoyaltiesPage />} />
        <Route path="/challenges" element={<ChallengesPage />} />
        <Route path="/crowdfund" element={<CrowdfundPage />} />
        <Route path="/sessions" element={<SessionsPage />} />
        <Route path="/sync" element={<SyncLicensingPage />} />
        <Route path="/tokyo" element={<TokyoStorePage />} />
        <Route path="/discover" element={<DiscoverPage />} />
        <Route path="/discover/mood/:moodName" element={<MoodBeatsPage />} />
        <Route path="/discover/collection/:collectionId" element={<CollectionBeatsPage />} />
        <Route path="/discover/playlist/:playlistId" element={<PlaylistPage />} />
        <Route path="/octane" element={<OctanePage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
      </Routes>
    </>
  );
}

// Import pages
import HomePage from "./pages/HomePage";
import BeatsPage from "./pages/BeatsPage";
import BeatDetailPage from "./pages/BeatDetailPage";
import MerchPage from "./pages/MerchPage";
import MerchDetailPage from "./pages/MerchDetailPage";
import GaragePage from "./pages/GaragePage";
import CartPage from "./pages/CartPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import AdminPage from "./pages/AdminPage";
import WishlistPage from "./pages/WishlistPage";
import NewsletterPopup from "./components/NewsletterPopup";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import OrdersPage from "./pages/OrdersPage";
import ProfilePage from "./pages/ProfilePage";
import LiveChat from "./components/LiveChat";
import BundlesPage from "./pages/BundlesPage";
import GalleryPage from "./pages/GalleryPage";
import GiftCardsPage from "./pages/GiftCardsPage";
import ProducerPage from "./pages/ProducerPage";
import ProducerStorefrontPage from "./pages/ProducerStorefrontPage";
import SoundKitsPage from "./pages/SoundKitsPage";
import SupportPage from "./pages/SupportPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import ContestsPage from "./pages/ContestsPage";
import BeatFeedPage from "./pages/BeatFeedPage";
import AIBeatsPage from "./pages/AIBeatsPage";
import BeatCustomizerPage from "./pages/BeatCustomizerPage";
import MerchStudioPage from "./pages/MerchStudioPage";
import BattlesPage from "./pages/BattlesPage";
import ProducerAnalyticsPage from "./pages/ProducerAnalyticsPage";
import RoyaltiesPage from "./pages/RoyaltiesPage";
import ChallengesPage from "./pages/ChallengesPage";
import CrowdfundPage from "./pages/CrowdfundPage";
import SessionsPage from "./pages/SessionsPage";
import SyncLicensingPage from "./pages/SyncLicensingPage";
import TokyoStorePage from "./pages/TokyoStorePage";
import DiscoverPage from "./pages/DiscoverPage";
import MoodBeatsPage from "./pages/MoodBeatsPage";
import CollectionBeatsPage from "./pages/CollectionBeatsPage";
import PlaylistPage from "./pages/PlaylistPage";
import FlashSaleBanner from "./components/FlashSaleBanner";
import OctanePage from "./pages/OctanePage";
import { HelmetProvider } from "./components/SEO";

export default App;
